import { AXIS_HIGH_UNBOUNDED_NUMBER, AXIS_LOW_UNBOUNDED_NUMBER } from 'flow/service/actions';
import type { ActionOption, MatrixModel, OutputValueRange } from '~/dashboard/views/workflows/store/engine/types';

export const transformCellValue = (
  matrixDataType: MatrixModel['matrixDataType'],
  value: string,
) => {
  if (!value) return null;

  switch (matrixDataType) {
    case 'number':
      // the user could input a string value in a numeric matrix's cell
      return isNaN(Number(value)) ? null : Number(value);
    case 'string':
      return String(value);
    default:
      return null;
  }
};

export const getShouldDefineValueRanges = (
  outputValueRange: ActionOption,
  defineValueRanges?: boolean,
) => (
  outputValueRange?.jsonValue && (
    defineValueRanges !== false // acceptable values are true or undefined
  )
);

export const getCellColor = ({
  cellValue,
  hasValueRangeConfig,
  outputValueRange,
}: {
  cellValue: number | null;
  hasValueRangeConfig: boolean;
  outputValueRange: OutputValueRange[];
}) => {
  const cellColor = hasValueRangeConfig && typeof cellValue === 'number'
    ? outputValueRange?.find(({ minValue, maxValue }, index, array) => {
      const isLastRange = index === array.length - 1;
      return (minValue === null && maxValue > cellValue)
        || (minValue <= cellValue && (isLastRange ? maxValue >= cellValue : maxValue > cellValue))
        || (minValue <= cellValue && maxValue === null);
    })?.color : null;
  return cellColor;
};

const handleCopyAxisHeader = async ({
  axis, cellIndex,
}: {
  axis: MatrixModel['axis']['x' | 'y'];
  cellIndex: number;
}) => {
  const { axisHeader, order } = axis;

  const clipboardText = order === null
    ? String(axisHeader[cellIndex])
    : `${axisHeader[cellIndex]},${axisHeader[cellIndex + 1]}`;
  const type = 'text/plain';
  const clipboardItemData = {
    [type]: clipboardText,
  };
  const clipboardItem = new ClipboardItem(clipboardItemData);
  await navigator.clipboard.write([clipboardItem]);
};

const handlePasteAxisHeader = async ({ axis, cellIndex, handleUpdateAxisHeader }: {
  axis: MatrixModel['axis']['x' | 'y'];
  cellIndex: number;
  handleUpdateAxisHeader: (newAxisHeader: MatrixModel['axis']['x' | 'y']['axisHeader']) => void;
}) => {
  const { axisHeaderDataType } = axis;
  const clipboardText = await navigator.clipboard.readText();

  const updatedAxisHeader = [...axis.axisHeader];
  // special handling for an axis with range configurations
  if (axis.order) {
    const clipboardValuesArray = clipboardText.split(',').map(textValue => Number(textValue));
    const isClipboardValueInvalidRange = (
      clipboardValuesArray.length !== 2
      || clipboardValuesArray.some(val => Number.isNaN(Number(val)))
    );

    if (isClipboardValueInvalidRange) return;

    const unboundValues = [AXIS_LOW_UNBOUNDED_NUMBER, AXIS_HIGH_UNBOUNDED_NUMBER];
    const firstValueUnbound = unboundValues.includes(clipboardValuesArray[0]);
    const lastValueUnbound = unboundValues.includes(clipboardValuesArray[1]);
    const pastingIntoFirstRow = cellIndex === 0;
    const pastingIntoLastRow = cellIndex === updatedAxisHeader.length - 2;

    if (firstValueUnbound && !pastingIntoFirstRow) {
      updatedAxisHeader.splice(cellIndex + 1, 1, clipboardValuesArray[1]);
    } else if (lastValueUnbound && !pastingIntoLastRow) {
      updatedAxisHeader.splice(cellIndex + 1, 1, clipboardValuesArray[0]);
    } else {
      updatedAxisHeader.splice(cellIndex, 2, ...clipboardValuesArray);
    }
    handleUpdateAxisHeader(updatedAxisHeader);
    return;
  }

  if (axisHeaderDataType === 'number') {
    const numberValue = Number(clipboardText);
    if (Number.isNaN(numberValue)) return;
    updatedAxisHeader.splice(cellIndex, 1, numberValue);
    handleUpdateAxisHeader(updatedAxisHeader);
    return;
  }

  updatedAxisHeader.splice(cellIndex, 1, clipboardText);
  handleUpdateAxisHeader(updatedAxisHeader);
};

export const handleCopyOrPasteAxisHeader = async ({
  axis, cellIndex, event, handleUpdateAxisHeader,
}: {
  axis: MatrixModel['axis']['x' | 'y'];
  cellIndex: number;
  event: any;
  handleUpdateAxisHeader: (newAxisHeader: MatrixModel['axis']['x' | 'y']['axisHeader']) => void;
}) => {
  const { key, ctrlKey, metaKey } = event;
  if (ctrlKey || metaKey) {
    if (key === 'c') {
      const { axisHeader, order } = axis;
      const invalidCopy = (
        axisHeader[cellIndex] === null
        || (order !== null && axisHeader[cellIndex + 1] === null)
      );
      if (invalidCopy) return;

      handleCopyAxisHeader({ axis, cellIndex });
    } else if (key === 'v') {
      handlePasteAxisHeader({ axis, cellIndex, handleUpdateAxisHeader });
    }
  }
};
